import { Box, Chip, SxProps, Theme, Typography, styled } from "@mui/material";
import { ReactElement } from "react";

import { ShortCodeConfig } from "~frontend/__generated__/graphql";
import { PageHeader } from "~frontend/components/PageHeader";
import Table, { TableBox } from "~frontend/components/Table";
import { palette } from "~frontend/theme";

type Row = {
  id: string;
  keyword: string;
  label: string;
  response: string;
};

const KeywordChip = styled(Chip)({
  marginLeft: 2,
  borderRadius: 0,
  backgroundColor: palette.lightOrange,
  fontWeight: 600,
  fontSize: "10px",
  padding: 0,
  height: "18px",

  "& .MuiChip-label": {
    padding: "4px",
  },
});

export default function FixedKeywords({
  shortcodeConfig,
  tableBoxSx = {},
}: {
  shortcodeConfig: ShortCodeConfig;
  tableBoxSx?: SxProps<Theme>;
}): ReactElement {
  const rows: Row[] = shortcodeConfig.optInKeywords
    .map(keyword => ({
      id: keyword,
      keyword,
      label: "Opt In",
      response: shortcodeConfig.optInMessage,
    }))
    .concat(
      shortcodeConfig.optOutKeywords.map(keyword => ({
        id: keyword,
        keyword,
        label: "Opt Out",
        response: shortcodeConfig.optOutMessage,
      }))
    )
    .concat(
      shortcodeConfig.helpKeywords.map(keyword => ({
        id: keyword,
        keyword,
        label: "Help",
        response: shortcodeConfig.helpMessage,
      }))
    );

  return (
    <Box>
      <PageHeader>Fixed Keywords</PageHeader>
      <Typography>
        These keywords are specified in your shortcode contract and can not be
        edited. If there is an issue with one of your fixed keywords, please
        reach out to{" "}
        <a href="mailto:support@scaletowin.com">support@scaletowin.com</a> to
        correct it.
      </Typography>
      <TableBox
        sx={{
          borderWidth: 2,
          borderColor: palette.lightGray,
          borderStyle: "solid",
          mt: 4,
          px: 4,
          pt: 2,
          pb: 4,
          ...tableBoxSx,
        }}
      >
        <Table
          rows={rows}
          columns={[
            {
              key: "keyword",
              title: "Keyword",
              align: "left",
              sx: { width: "200px" },
              renderValue: row => (
                <>
                  {row.keyword} <KeywordChip label={row.label} />
                </>
              ),
            },
            {
              key: "message",
              title: "Message",
              align: "left",
              renderValue: row => <>{row.response}</>,
            },
          ]}
          noFooter
        />
      </TableBox>
    </Box>
  );
}
